import React from 'react';
import Datetime from 'react-datetime';
import { WrappedComponentProps, injectIntl, FormattedMessage } from 'react-intl';
import moment, { Moment } from 'moment';
import 'moment/locale/da';
import 'moment/locale/en-ie';
// Assets
import images from '../../../assets/images/images';
// Interfaces
import { INonEventData } from '../../../interfaces/general.interfaces';
// Enums
import { DateTimeFormat, LanguageLocaleMapper } from '../../../enums';
// Helpers
import { isValidDateAndTime, getDateTimeTemplate } from '../../../helpers/dateTime';
// Styles
import './datetime.scss';
import { Image } from '../..';
import { InputError } from '..';

interface IDateTimePickerProps extends WrappedComponentProps {
    name: string;
    format: DateTimeFormat;
    value?: string;
    disabled?: boolean;
    placeholder?: string;
    label?: string;
    touched?: boolean;
    required?: boolean;
    autoComplete?: string;
    className?: string;
    externalUpdateValue?: string;
    setCurrentTime?: boolean;
    triggerClear?: boolean;
    language: string;
    dateValidation?: (currentDate: Moment) => boolean;
    onChange?: (event: INonEventData, isValid: boolean) => void;
    onBlur?: () => void;
    onKeyDown?: (event: React.KeyboardEvent<Element>) => void;
    checkShownErrors?: (name: string, errorMessage: string) => void;
    errorHandler?: (value: string | Moment) => string;
}

interface IDateTimePickerState {
    value: string | Moment;
    stringifiedValue: string;
    errorMessage: string;
    touched: boolean;
}

class _DateTimePicker extends React.Component<IDateTimePickerProps, IDateTimePickerState> {
    inputElementRef: HTMLLabelElement = null;
    timeoutId: number;

    constructor(props: IDateTimePickerProps) {
        super(props);

        this.state = this.getInitialState();
    }

    componentDidMount() {
        this.checkErrors(this.state.value);
    }

    componentWillUnmount() {
        clearTimeout(this.timeoutId);
    }

    componentDidUpdate(prevProps: IDateTimePickerProps) {
        const { touched, disabled, required, externalUpdateValue, setCurrentTime, value, format, language } =
            this.props;
        const template = getDateTimeTemplate(format, language);
        if (
            touched !== prevProps.touched ||
            disabled !== prevProps.disabled ||
            required !== prevProps.required ||
            (language !== prevProps.language && this.state.errorMessage) ||
            (this.state.touched && prevProps.externalUpdateValue !== externalUpdateValue)
        ) {
            this.checkErrors(this.state.value);
        }

        if (disabled !== prevProps.disabled || (!value && this.state.stringifiedValue !== value)) {
            const currentTimeSetUp = !disabled && setCurrentTime;
            const currentDateTime = this.getCurrentDateTime();
            const newValue = currentTimeSetUp ? currentDateTime.format(template) : value;

            this.onChangeValue(newValue);
        }

        if (language !== prevProps.language && value) {
            this.onLocaleChange();
        }

        if (prevProps.value !== this.props.value) {
            this.onChangeValue(this.props.value);
        }

        if (!prevProps.triggerClear && this.props.triggerClear) {
            clearTimeout(this.timeoutId);
            this.setState({
                ...this.getInitialState(),
                value: '',
            });
        }
    }

    getInitialState = () => ({
        value: this.props.value || '',
        stringifiedValue: '',
        errorMessage: '',
        touched: false,
    });

    getCurrentDateTime = () => {
        return moment().add(2, 'minutes').locale(LanguageLocaleMapper[this.props.language]);
    };

    checkErrors = (value: string | Moment) => {
        const { checkShownErrors, name, touched } = this.props;
        const errorMessage = this.errorHandler(value);

        this.setState((state) => ({
            errorMessage,
            touched: state.touched || touched,
        }));

        if (checkShownErrors) {
            checkShownErrors(name, errorMessage);
        }
    };

    handleIconClick = () => {
        const inputElement = this.inputElementRef.control;

        inputElement && inputElement.focus();
    };

    onChangeValue = (value: string | Moment) => {
        const { onChange: propsOnChange, name, format, language } = this.props;
        const errorMessage = this.errorHandler(value);
        const template = getDateTimeTemplate(format, language);
        const stringValue = moment.isMoment(value) ? value.format(template) : value;

        this.setState({ stringifiedValue: stringValue, value });
        this.checkErrors(value);

        if (propsOnChange) {
            const nonEvent = { currentTarget: { value: stringValue, name } };

            propsOnChange(nonEvent, !errorMessage);
        }
    };

    onLocaleChange = () => {
        if (this.inputElementRef) {
            const inputElement = this.inputElementRef.control;
            const inputElementValue = inputElement.getAttribute('value');

            this.onChangeValue(inputElementValue);
        }
    };

    onOpen = () => {
        // Fix case when input is focused and calendar icon is clicked
        clearTimeout(this.timeoutId);
    };

    onInputBlur = () => {
        this.timeoutId = window.setTimeout(() => {
            if (!this.state.touched) {
                this.setState({ touched: true }, () => {
                    this.checkErrors(this.state.value);
                });
            }
        }, 100);
    };

    errorHandler = (value: string | Moment) => {
        const {
            intl: { formatMessage },
            required,
            errorHandler,
            format,
            language,
        } = this.props;
        const template = getDateTimeTemplate(format, language);

        if (required && typeof value === 'string' && !value.trim()) {
            return formatMessage({ id: 'REQUIRED_FIELD' });
        } else if (required && !moment.isMoment(value) && !isValidDateAndTime(value, template, language)) {
            return formatMessage({ id: format + '_INVALID_FORMAT' }, { template });
        } else if (
            !required &&
            typeof value === 'string' &&
            value.trim() &&
            !isValidDateAndTime(value, template, language)
        ) {
            return formatMessage({ id: format + '_INVALID_FORMAT' }, { template });
        } else if (errorHandler) {
            return errorHandler(value);
        }

        return '';
    };

    getMomentDataFromDateTimePicker = (): Moment => {
        const { value } = this.state;

        return moment.isMoment(value) ? value : null;
    };

    specifyTimeDateFormat = () => {
        const { format, language } = this.props;

        switch (format) {
            case DateTimeFormat.DATE_AND_TIME:
                return {
                    dateFormat: getDateTimeTemplate(DateTimeFormat.ONLY_DATE, language),
                    timeFormat: getDateTimeTemplate(DateTimeFormat.ONLY_TIME, language),
                };

            case DateTimeFormat.ONLY_DATE:
                return {
                    dateFormat: getDateTimeTemplate(DateTimeFormat.ONLY_DATE, language),
                    timeFormat: false,
                };

            case DateTimeFormat.ONLY_TIME:
                return {
                    dateFormat: false,
                    timeFormat: getDateTimeTemplate(DateTimeFormat.ONLY_TIME, language),
                };

            default:
                return null;
        }
    };

    render() {
        const {
            format,
            name,
            intl,
            disabled,
            setCurrentTime,
            placeholder,
            dateValidation,
            required,
            className,
            language,
        } = this.props;
        const { value, errorMessage, touched: stateTouched } = this.state;
        const onlyTime = format === DateTimeFormat.ONLY_TIME;
        const icon = onlyTime ? images.clock : images.calendar;
        const hasError = errorMessage && stateTouched && !disabled;
        const dateTimePickerFormat = this.specifyTimeDateFormat();
        const locale = LanguageLocaleMapper[language];
        moment.locale(locale, { week: { dow: 1, doy: 4 } }); // Set first day of the week and first week of the year

        return (
            <div className={`${className ? className : ''} rdtWrapper`}>
                <Datetime
                    locale={locale || LanguageLocaleMapper['en']}
                    timeFormat={dateTimePickerFormat && dateTimePickerFormat.timeFormat}
                    dateFormat={dateTimePickerFormat && dateTimePickerFormat.dateFormat}
                    className={`font-normalizer ${onlyTime ? 'rdtTimeOnly' : ''}`}
                    closeOnSelect={!!value}
                    inputProps={{
                        id: name,
                        name,
                        type: 'search',
                        placeholder: intl.formatMessage({ id: placeholder }),
                        className: `rdtInput ${hasError ? 'withError' : ''}`,
                        autoComplete: 'off',
                        onKeyDown: this.props.onKeyDown,
                        disabled,
                        required,
                        onBlur: this.onInputBlur,
                    }}
                    value={value}
                    input
                    onChange={this.onChangeValue}
                    isValidDate={dateValidation}
                    initialViewDate={setCurrentTime ? this.getCurrentDateTime() : null}
                    onOpen={this.onOpen}
                />
                <i className="rdtIcon">
                    <Image src={icon} onClick={this.handleIconClick} />
                </i>
                <label className="hidden" htmlFor={name} ref={(l) => (this.inputElementRef = l)}>
                    <FormattedMessage id="DATE_TIME_INPUT_FIELD" />
                </label>
                {hasError && <InputError>{errorMessage}</InputError>}
            </div>
        );
    }
}

export const DateTimePicker = injectIntl(_DateTimePicker);
